/* Research (R&D) — a faithful React port of the prototype's rRnd screen.
 *
 * Set a quarterly R&D budget (converted to research points with diminishing
 * returns), then pick a path through a 4-branch × 5-tier tech tree: one active
 * node plus a queue that auto-starts the next. Switching nodes banks progress, so
 * nothing is lost. Each node shows its cost, prerequisites, estimated time at the
 * current budget, and which rivals have already completed it.
 *
 * Budget / active / queue are staged in the decisions draft (store.setRnd →
 * d.rnd) overlaid on the server's resolved R&D state; the server's applyDecisions
 * replays the same intent and Engine.advance banks RP. Sub-sections are plain
 * render functions (not nested components) so the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { Engine, applyGameCfg, TECH_TREE, TECH_BY_ID, TECH_BRANCHES, COMPETITORS, RP_PER_QTR, fmtM, clamp } from '@venturewrist/engine';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import type { ScreenProps } from './types';
import '../../styles/rnd.css';

const Eng = Engine as any;
const TREE = TECH_TREE as any[];
const BY_ID = TECH_BY_ID as Record<string, any>;
const BRANCHES = TECH_BRANCHES as any[];
const COMPALL = COMPETITORS as any[];

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v }: { v: number }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%' }} /></div>
);

export function RnD({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const setRnd = useDecisions((z) => z.setRnd);

  // Working game with the staged R&D applied the way the server applies it
  // (packages/engine/src/apply.js#applyRnd): a queued node whose prerequisites
  // aren't met by done ∪ active ∪ the queue ahead of it is dropped here exactly
  // as it would be at advance, so the screen never shows a plan that won't run.
  // bank + done (which only change at advance) stay from the server state.
  const g = useMemo(() => {
    const wg = buildWorkingGame(detail.state, draft);
    applyGameCfg(wg);
    Eng.normalizeRnd(wg);
    return wg;
  }, [detail.state, draft]);

  // effective (staged-over-server) budget / active / queue
  const budget: number = g.rnd.budget ?? 0;
  const queue: string[] = g.rnd.queue ?? [];
  const active: string | null = g.rnd.active ?? null;

  const round = g.round as number;
  const rp = RP_PER_QTR(budget);
  const shortGame = (g.maxRounds || 8) < 10;
  const done: string[] = g.rnd.done ?? [];
  const bank: Record<string, number> = g.rnd.bank ?? {};
  const nComps = g.numComps ? Math.max(1, Math.min(g.numComps, COMPALL.length)) : 3;
  const comps = COMPALL.slice(0, nComps);

  const stateOf = (id: string): string => {
    if (done.includes(id)) return 'done';
    if (active === id) return 'active';
    if (queue.includes(id)) return 'queued';
    return (BY_ID[id].req as string[]).every((q) => done.includes(q)) ? 'available' : 'locked';
  };
  const canQueue = (id: string): boolean => {
    if (done.includes(id) || active === id || queue.includes(id)) return false;
    const have = new Set([...done, ...(active ? [active] : []), ...queue]);
    return (BY_ID[id].req as string[]).every((q) => have.has(q));
  };

  // ── store actions (staged) ──────────────────────────────────────────────────
  const setBudget = (v: number) => setRnd({ budget: Math.round((v || 0) * 1e6) });
  // Like the prototype's setRndActive/queueRnd: starting research with no
  // budget would never progress, so a zero budget defaults to $1.5M.
  const seedBudget = budget > 0 ? {} : { budget: 1.5e6 };
  const setActive = (id: string) => setRnd({ active: id, ...seedBudget });
  const stop = () => setRnd({ active: null });
  const enqueue = (id: string) => setRnd({ queue: [...queue, id], ...seedBudget });
  const dequeue = (id: string) => setRnd({ queue: queue.filter((x) => x !== id) });

  // ── budget + active/queue control ─────────────────────────────────────────────
  const node = active ? BY_ID[active] : null;
  const have = node ? bank[active!] || 0 : 0;
  const eta = node ? Eng.techEtaQtrs(g, active) : null;
  void round;

  const controlCard = () => (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="spread"><b>Research budget</b><b style={{ color: 'var(--acc)' }}>{fmtM(budget)} /qtr</b></div>
          <input type="range" min={0} max={4} step={0.1} value={budget / 1e6} style={{ width: '100%', margin: '10px 0' }}
            onChange={(e) => setBudget(parseFloat(e.target.value))} aria-label="R&D budget" />
          <div className="spread sub"><span>$0</span><span>⚗ <b style={{ color: 'var(--acc)' }}>{Math.round(rp)} RP/qtr</b></span><span>$4M</span></div>
          <div className="sub" style={{ marginTop: 8 }}>Research Points (RP) = 100 × (budget ⁄ $1M)<sup>0.8</sup> — two steady $1M quarters (200 RP) beat one $2M crash quarter (174 RP). Spend flows into the quarterly P&amp;L's R&D line. No active or queued node ⇒ budget auto-suspended.</div>
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          {node ? (
            <>
              <div className="spread"><b>{node.icon} {node.name}</b><span className="chip acc">T{node.tier} · researching</span></div>
              <div className="sub" style={{ margin: '6px 0' }}>{node.desc}</div>
              <div className="spread sub"><span>{Math.round(have)} / {node.rp} RP</span><span>{budget > 0 ? '~' + eta + ' qtr' + (eta === 1 ? '' : 's') + ' left' : 'set a budget to progress'}</span></div>
              <Bar v={clamp((have / node.rp) * 100, 0, 100)} />
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 9 }} onClick={stop}>{have > 0 ? 'Stop — keep ' + Math.round(have) + ' RP of progress' : 'Cancel'}</button>
            </>
          ) : (
            <div className="sub" style={{ padding: '10px 0' }}><b>No active research.</b> Pick a node below to begin. Switching nodes saves your progress — nothing is lost.</div>
          )}
          {queue.length ? (
            <div style={{ marginTop: 12 }}>
              <div className="sub" style={{ marginBottom: 5 }}>Queue (auto-starts next):</div>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {queue.map((id, i) => (
                  <span key={id} className="chip">{i + 1}. {BY_ID[id].icon} {BY_ID[id].name} <span style={{ cursor: 'pointer', color: 'var(--bad)' }} onClick={() => dequeue(id)}>✕</span></span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  // ── one tech node ──────────────────────────────────────────────────────────────
  const techNode = (n: any) => {
    const state = stateOf(n.id);
    const rivals = comps.filter((c) => (Eng.rivalDone(g, c.id) as string[]).includes(n.id));
    const pips = rivals.map((c) => <span key={c.id} className="tnode-dot" title={c.name + ' completed this'} style={{ background: c.color }} />);
    const cap = n.cap ? ' ★' : '';
    const reqNames = n.req.length ? n.req.map((q: string) => BY_ID[q].name).join(' + ') : '—';
    const eta2 = state === 'available' ? Eng.techEtaQtrs(g, n.id) : null;
    const tip = `${n.name} (T${n.tier}, ${n.rp} RP) — ${n.desc} · requires: ${reqNames}${eta2 && eta2 < Infinity ? ' · ~' + eta2 + ' qtr' + (eta2 === 1 ? '' : 's') + ' at current budget' : ''}`;
    const saved = bank[n.id] || 0;

    let badge: any = null;
    let foot: any = null;
    if (state === 'done') {
      badge = <span className="chip good" style={{ fontSize: 9.5, padding: '1px 6px' }}>✓</span>;
    } else if (state === 'active') {
      badge = <span className="chip acc" style={{ fontSize: 9.5, padding: '1px 6px' }}>◉</span>;
      foot = <><div className="sub" style={{ fontSize: 10 }}>{Math.round(saved)}/{n.rp} RP</div><Bar v={clamp((saved / n.rp) * 100, 0, 100)} /></>;
    } else if (state === 'queued') {
      badge = <span className="chip" style={{ fontSize: 9.5, padding: '1px 6px' }}>#{queue.indexOf(n.id) + 1}</span>;
      foot = <button className="btn btn-ghost btn-sm" style={{ width: '100%', fontSize: 10, padding: 3 }} onClick={() => dequeue(n.id)}>remove</button>;
    } else if (state === 'available') {
      badge = saved > 0
        ? <span className="chip acc" style={{ fontSize: 9.5, padding: '1px 6px' }}>{Math.round(saved)}/{n.rp}</span>
        : <span className="chip" style={{ fontSize: 9.5, padding: '1px 6px' }}>{n.rp} RP</span>;
      foot = (
        <>
          {saved > 0 ? <><div className="sub" style={{ fontSize: 10 }}>{Math.round(saved)}/{n.rp} RP progress saved</div><Bar v={clamp((saved / n.rp) * 100, 0, 100)} /></> : null}
          <div className="row" style={{ gap: 4, marginTop: 4 }}>
            <button className="btn btn-pri btn-sm" style={{ flex: 1, fontSize: 10, padding: 3 }} onClick={() => setActive(n.id)}>{saved > 0 ? 'Resume' : 'Research'}</button>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 10, padding: 3 }} onClick={() => enqueue(n.id)}>Queue</button>
          </div>
          {eta2 && eta2 < Infinity ? <div className="sub" style={{ fontSize: 10, textAlign: 'center', marginTop: 2 }}>~{eta2} qtr{eta2 === 1 ? '' : 's'}</div> : null}
        </>
      );
    } else {
      badge = <span className="chip" style={{ fontSize: 9.5, padding: '1px 6px' }}>🔒</span>;
      foot = <><div className="sub" style={{ fontSize: 10 }}>needs {reqNames}</div>{canQueue(n.id) ? <button className="btn btn-ghost btn-sm" style={{ width: '100%', fontSize: 10, padding: 3, marginTop: 3 }} onClick={() => enqueue(n.id)}>Queue</button> : null}</>;
    }
    // a stopped-but-funded available node shows the accent "progress saved" border
    const dcls = state === 'available' && saved > 0 ? 'active' : state;
    return (
      <div key={n.id} className={'tnode tn-' + dcls} title={tip}>
        <div className="spread" style={{ gap: 4 }}><b style={{ fontSize: 11.5, lineHeight: 1.2 }}>{n.icon} {n.name}{cap}</b>{badge}</div>
        {pips.length ? <div style={{ margin: '3px 0' }}>{pips}</div> : null}
        {foot}
      </div>
    );
  };

  return (
    <div className="rd-root">
      <div className="screen-head">
        <div className="spread" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2>🧪 Research<WhyChip /></h2>
            <div className="sub">Set a quarterly R&amp;D budget — it converts to <b>research points</b> with diminishing returns. Pick a path through the tree: one node at a time, with a queue. Deep beats wide.</div>
          </div>
        </div>
      </div>

      {controlCard()}

      <div className="row sub" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
        {comps.map((c) => <span key={c.id} className="tnode-dot" title={c.name} style={{ background: c.color }} />)}
        <span>= rival completed — hover a dot for who</span>
      </div>
      <div className="sub" style={{ marginBottom: 10, fontSize: 11.5 }}>💡 Hover any node to see its effect, RP cost and estimated time at your current budget.</div>

      {BRANCHES.map((br) => (
        <div className="lane" key={br.id}>
          <div className="lane-head">{br.icon} {br.name}</div>
          <div className="lane-track">
            {[1, 2, 3, 4, 5].map((t) => {
              const nodes = TREE.filter((n) => n.branch === br.id && n.tier === t);
              const dim = shortGame && t === 5;
              return (
                <div key={t} style={{ display: 'flex', alignItems: 'stretch' }}>
                  {t > 1 ? <div className="lane-arrow">→</div> : null}
                  <div className={'tier-col' + (dim ? ' tier-dim' : '')}>
                    {nodes.map(techNode)}
                    {dim ? <div className="sub" style={{ fontSize: 10, textAlign: 'center', marginTop: 2 }}>long campaign</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
